import React, { useEffect, useRef } from "react";
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import {
  Button,
  ChunkLoadBoundary,
  Logo,
  PageSkeleton,
  ToastProvider,
  type PageSkeletonVariant,
} from "@mbbe/ui";
import { CustomerAuthProvider, useCustomerAuth } from "./CustomerAuthContext";
import { usePortalCopy } from "./usePortalCopy";
import { useLanguage } from "../contexts/LanguageContext";
import { PortalLayout } from "./PortalLayout";
import { PortalLanguagePreferenceProvider } from "./PortalLanguagePreference";
import {
  readStoredPortalLanguage,
  writeStoredPortalLanguage,
} from "./lib/languagePreference";
import { PortalProjectProvider, usePortalProject } from "./PortalProjectContext";
import { DestekLayout } from "../destek/DestekLayout";
import NotFound from "./pages/NotFound";
import {
  hasPortalCapability,
  hasPortalProjectGrant,
  type PortalCapability,
} from "./lib/portalAccess";
import { SupportSurfaceProvider } from "../destek/SupportSurfaceContext";
import { revokeStoredNotificationDevice } from "../destek/lib/notificationsApi";
import {
  clearPortalActivitySession,
  recordPortalLogin,
} from "./lib/portalActivityApi";

const Login = React.lazy(() => import("./pages/Login"));
const Dashboard = React.lazy(() => import("./pages/Dashboard"));
const IssueList = React.lazy(() => import("../destek/pages/IssueList"));
const NewIssue = React.lazy(() => import("../destek/pages/NewIssue"));
const IssueDetail = React.lazy(() => import("../destek/pages/IssueDetail"));
const Tasks = React.lazy(() => import("./pages/Tasks"));
const TaskDetail = React.lazy(() => import("./pages/TaskDetail"));
const Payments = React.lazy(() => import("./pages/Payments"));
const PaymentDetail = React.lazy(() => import("./pages/PaymentDetail"));
const Reports = React.lazy(() => import("./pages/Reports"));
const ReportDetail = React.lazy(() => import("./pages/ReportDetail"));
const Files = React.lazy(() => import("./pages/Files"));
const Documents = React.lazy(() => import("./pages/Documents"));
const Approvals = React.lazy(() => import("./pages/Approvals"));
const Meetings = React.lazy(() => import("./pages/Meetings"));
const Commercial = React.lazy(() => import("./pages/Commercial"));
const Team = React.lazy(() => import("./pages/Team"));
const InviteAcceptance = React.lazy(() => import("./pages/InviteAcceptance"));

export function resolvePortalSkeletonVariant(
  pathname: string,
): PageSkeletonVariant {
  if (
    pathname === "/login" ||
    pathname === "/invite" ||
    pathname.startsWith("/invite/") ||
    pathname === "/support/new"
  ) {
    return "form";
  }
  if (
    pathname.startsWith("/support/issue/") ||
    /^\/(tasks|payments|reports|approvals)\/[^/]+$/.test(pathname)
  ) {
    return "detail";
  }
  if (pathname === "/" || pathname === "/commercial") return "dashboard";
  return "list";
}

function Loading() {
  const { language } = usePortalCopy();
  const { pathname } = useLocation();
  return (
    <main className="relative flex min-h-screen items-center justify-center overflow-hidden bg-dark-900 px-4 py-12 text-white">
      <div aria-hidden className="pointer-events-none absolute inset-0 bg-[radial-gradient(55rem_35rem_at_15%_-5%,rgba(59,130,246,0.16),transparent_62%),linear-gradient(120deg,transparent_45%,rgba(30,41,59,0.34))]" />
      <div className="relative w-full max-w-5xl">
        <div className="mb-8 flex items-center gap-3">
          <Logo variant="contained" className="h-11 w-11" />
          <div>
            <p className="font-bold">mbbe</p>
            <p className="text-[10px] uppercase tracking-[0.18em] text-brand-300">
              {language === "tr" ? "Müşteri Portalı" : "Client Portal"}
            </p>
          </div>
        </div>
        <PageSkeleton
          variant={resolvePortalSkeletonVariant(pathname)}
          label={
            language === "tr"
              ? "Müşteri portalı hazırlanıyor"
              : "Preparing client portal"
          }
        />
      </div>
    </main>
  );
}

function PortalContentLoading({ legacy = false }: { legacy?: boolean }) {
  const { language } = usePortalCopy();
  const { pathname } = useLocation();
  const skeleton = (
    <PageSkeleton
      variant={resolvePortalSkeletonVariant(pathname)}
      label={
        legacy
          ? language === "tr"
            ? "Destek alanı yükleniyor"
            : "Loading support workspace"
          : language === "tr"
            ? "Portal sayfası yükleniyor"
            : "Loading portal page"
      }
    />
  );

  return legacy ? <DestekLayout>{skeleton}</DestekLayout> : skeleton;
}

export function Protected({ children }: { children: React.ReactNode }) {
  const {
    user,
    isCustomer,
    isLoading,
    profileError,
    accessDenied,
    retryProfile,
  } = useCustomerAuth();
  const { p } = usePortalCopy();
  if (isLoading) return <Loading />;
  if (user && profileError)
    return (
      <div className="flex min-h-screen items-center justify-center bg-dark-900 px-4 text-center text-white">
        <div className="max-w-md">
          <h1 className="text-2xl font-bold">{p("profileError")}</h1>
          <p className="mt-3 text-sm leading-6 text-slate-400">
            {p("profileErrorDescription")}
          </p>
          <Button variant="primary" onClick={retryProfile} className="mt-6">
            {p("retry")}
          </Button>
        </div>
      </div>
    );
  if (user && accessDenied) return <PortalAccessDenied />;
  if (!user || !isCustomer) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

export function PortalAccessDenied() {
  const { p } = usePortalCopy();
  return (
    <div role="alert" className="mx-auto max-w-xl py-12">
      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-brand-300">
        403
      </p>
      <h1 className="mt-3 text-2xl font-bold text-white">
        {p("accessDeniedTitle")}
      </h1>
      <p className="mt-4 text-sm leading-6 text-slate-400">
        {p("accessDeniedDescription")}
      </p>
    </div>
  );
}

export function PortalModuleAccess({
  capability,
  children,
}: {
  capability: PortalCapability;
  children: React.ReactNode;
}) {
  const { me } = useCustomerAuth();
  const [searchParams] = useSearchParams();
  const project = usePortalProject();
  // Account-wide modules ignore the project parameter, so a stale or revoked
  // value must not deny them.
  const projectId = project.scope === "account" ? null : searchParams.get("project");
  if (
    !hasPortalCapability(me, capability) ||
    !hasPortalProjectGrant(me, projectId)
  )
    return <PortalAccessDenied />;
  // Project-capable modules never fall back to another project: a malformed,
  // missing, deleted or revoked project is denied, and nothing renders (or is
  // requested) until the shared directory has confirmed the project.
  if (project.scope !== "account") {
    if (project.blocked) return <PortalAccessDenied />;
    if (project.status === "loading") return <PortalContentLoading />;
  }
  return <>{children}</>;
}

function PortalRoutes() {
  return (
    <ChunkLoadBoundary>
      <React.Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/invite/:token" element={<InviteAcceptance />} />
          <Route path="/invite" element={<InviteAcceptance />} />
          <Route element={<PortalAuthenticatedShell />}>
            <Route
              path="/"
              element={
                <PortalModuleAccess capability="dashboard">
                  <Dashboard />
                </PortalModuleAccess>
              }
            />
            <Route
              path="/support"
              element={
                <PortalModuleAccess capability="support">
                  <IssueList />
                </PortalModuleAccess>
              }
            />
            <Route
              path="/support/new"
              element={
                <PortalModuleAccess capability="support">
                  <NewIssue />
                </PortalModuleAccess>
              }
            />
            <Route
              path="/support/issue/:id"
              element={
                <PortalModuleAccess capability="support">
                  <IssueDetail />
                </PortalModuleAccess>
              }
            />
            <Route
              path="/tasks"
              element={
                <PortalModuleAccess capability="tasks">
                  <Tasks />
                </PortalModuleAccess>
              }
            />
            <Route
              path="/tasks/:id"
              element={
                <PortalModuleAccess capability="tasks">
                  <TaskDetail />
                </PortalModuleAccess>
              }
            />
            <Route
              path="/payments"
              element={
                <PortalModuleAccess capability="payments">
                  <Payments />
                </PortalModuleAccess>
              }
            />
            <Route
              path="/payments/:id"
              element={
                <PortalModuleAccess capability="payments">
                  <PaymentDetail />
                </PortalModuleAccess>
              }
            />
            <Route
              path="/reports"
              element={
                <PortalModuleAccess capability="reports">
                  <Reports />
                </PortalModuleAccess>
              }
            />
            <Route
              path="/reports/:id"
              element={
                <PortalModuleAccess capability="reports">
                  <ReportDetail />
                </PortalModuleAccess>
              }
            />
            <Route
              path="/files"
              element={
                <PortalModuleAccess capability="files">
                  <Files />
                </PortalModuleAccess>
              }
            />
            <Route
              path="/documents"
              element={
                <PortalModuleAccess capability="documentation">
                  <Documents />
                </PortalModuleAccess>
              }
            />
            <Route path="/approvals" element={<PortalModuleAccess capability="approvals"><Approvals /></PortalModuleAccess>} />
            <Route path="/approvals/:id" element={<PortalModuleAccess capability="approvals"><Approvals /></PortalModuleAccess>} />
            <Route
              path="/meetings"
              element={
                <PortalModuleAccess capability="meetings">
                  <Meetings />
                </PortalModuleAccess>
              }
            />
            <Route
              path="/commercial"
              element={
                <PortalModuleAccess capability="commercial">
                  <Commercial />
                </PortalModuleAccess>
              }
            />
            <Route
              path="/team"
              element={
                <OwnerOnly>
                  <Team />
                </OwnerOnly>
              }
            />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </React.Suspense>
    </ChunkLoadBoundary>
  );
}

export function OwnerOnly({ children }: { children: React.ReactNode }) {
  const { me } = useCustomerAuth();
  return me?.member?.role === "owner" ? (
    <>{children}</>
  ) : (
    <PortalAccessDenied />
  );
}

export function PortalAuthenticatedShell() {
  const { me } = useCustomerAuth();
  const location = useLocation();
  const legacy = me?.organization?.supportSurface === "legacy_destek";
  const allowedLegacyPath = location.pathname === "/support" ||
    location.pathname === "/support/new" ||
    location.pathname.startsWith("/support/issue/");
  return (
    <Protected>
      <PortalActivityRecorder />
      {legacy ? (
        allowedLegacyPath ? (
          <SupportSurfaceProvider surface="legacyPortal">
            <React.Suspense fallback={<PortalContentLoading legacy />}>
              <Outlet />
            </React.Suspense>
          </SupportSurfaceProvider>
        ) : <Navigate to="/support" replace />
      ) : (
        <SupportSurfaceProvider surface="portal">
          <PortalProjectProvider>
            <PortalLayout>
              <React.Suspense fallback={<PortalContentLoading />}>
                <Outlet />
              </React.Suspense>
            </PortalLayout>
          </PortalProjectProvider>
        </SupportSurfaceProvider>
      )}
    </Protected>
  );
}

function PortalActivityRecorder() {
  useEffect(() => {
    void recordPortalLogin().catch(() => undefined);
  }, []);
  return null;
}

export function PortalLanguagePersistence({
  children,
}: {
  children: React.ReactNode;
}) {
  const { language, setLanguage } = useLanguage();
  const skipInitialPersist = useRef(true);
  useEffect(() => {
    const stored = readStoredPortalLanguage();
    if (stored) setLanguage(stored);
  }, []);
  useEffect(() => {
    if (skipInitialPersist.current) {
      skipInitialPersist.current = false;
      return;
    }
    writeStoredPortalLanguage(language);
  }, [language]);
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);
  return (
    <PortalLanguagePreferenceProvider>{children}</PortalLanguagePreferenceProvider>
  );
}

export async function clearPortalCustomerSession() {
  await revokeStoredNotificationDevice();
  clearPortalActivitySession();
}

export default function PortalApp() {
  return (
    <ToastProvider>
      <PortalLanguagePersistence>
        <CustomerAuthProvider beforeSignOut={clearPortalCustomerSession}>
          <BrowserRouter>
            <PortalRoutes />
          </BrowserRouter>
        </CustomerAuthProvider>
      </PortalLanguagePersistence>
    </ToastProvider>
  );
}
